import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Moon, Sun, User, Shield, Users, LogOut, Key, Package, FolderOpen, ChevronDown, ChevronRight, Database, Server, FileText, Briefcase, Lightbulb } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import { useAuth } from '../utils/auth/AuthContext';
import { getCurrentUserPermissions } from '../utils/auth/authService';

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, isDarkMode, toggleTheme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    dataProcessing: true,
    infrastructure: true,
    reporting: true,
    insights: true,
    security: true
  });

  useEffect(() => {
    if (isAuthenticated) {
      loadUserRoles();
    }
  }, [isAuthenticated, user]);

  const loadUserRoles = async () => {
    try {
      const data = await getCurrentUserPermissions();
      const roleNames = data.roles.map((role) => role.name);
      setUserRoles(roleNames);
    } catch (err) {
      console.error('Failed to load user roles:', err);
    }
  };

  const canViewAuditLogs = userRoles.includes('admin') || userRoles.includes('auditor');

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const navItemStyle = (active: boolean): React.CSSProperties => ({
    padding: '10px 12px',
    borderRadius: '6px',
    textDecoration: 'none',
    color: active ? theme.activeText : theme.textSecondary,
    background: active ? theme.activeBackground : 'transparent',
    fontWeight: 600,
    fontSize: 14,
    display: 'block'
  });

  const sectionHeaderStyle: React.CSSProperties = {
    padding: '10px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: theme.text,
    fontWeight: 700,
    fontSize: 13,
    textTransform: 'uppercase',
    background: 'transparent',
    border: 'none',
    width: '100%',
    marginTop: 8
  };

  const SectionHeader = ({ 
    title, 
    icon: Icon, 
    sectionKey, 
    isExpanded 
  }: { 
    title: string; 
    icon: React.ComponentType<any>; 
    sectionKey: string; 
    isExpanded: boolean;
  }) => (
    <button
      onClick={() => toggleSection(sectionKey)}
      style={sectionHeaderStyle}
    >
      <Icon size={16} />
      <span style={{ flex: 1, textAlign: 'left' }}>{title}</span>
      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
    </button>
  );

  return (
    <aside style={{ height: '100vh', display: 'flex', flexDirection: 'column' ,width: '240px' }}>
      <div style={{ padding: '20px 16px', borderBottom: `1px solid ${theme.border}` }}>
          <img
              src={isDarkMode ? "/zell-analytics-white-transparent.png" : "/zell-analytics-black-transparent.png"}
              alt="Zell Analytics Logo"
              style={{
                  width: '100%',
                  marginBottom: '5px'
              }}
          />
      </div>
      <nav style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6, flex: 1, overflowY: 'auto' }}>
        <Link to="/" style={navItemStyle(location.pathname === '/')}>Main</Link>
        
        {/* Data Processing Section */}
        <SectionHeader 
          title="Data Processing" 
          icon={Database} 
          sectionKey="dataProcessing" 
          isExpanded={expandedSections.dataProcessing} 
        />
        {expandedSections.dataProcessing && (
          <div style={{ paddingLeft: 12 }}>
              <Link to="/data-quality" style={{...navItemStyle(location.pathname.startsWith('/data-quality')), opacity: 0.5, cursor: 'not-allowed'}}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                     Data Quality
                  </div>
              </Link>
            <Link to="/jobs" style={navItemStyle(location.pathname.startsWith('/jobs'))}>Jobs</Link>
            <Link to="/pipelines" style={navItemStyle(location.pathname.startsWith('/pipelines'))}>Pipelines</Link>
            <Link to="/chains" style={navItemStyle(location.pathname.startsWith('/chains'))}>Chains</Link>
            <Link to="/files" style={navItemStyle(location.pathname.startsWith('/files'))}>Files</Link>
          </div>
        )}

        {/* Infrastructure Section */}
        <SectionHeader 
          title="Infrastructure" 
          icon={Server} 
          sectionKey="infrastructure" 
          isExpanded={expandedSections.infrastructure} 
        />
        {expandedSections.infrastructure && (
          <div style={{ paddingLeft: 12 }}>
            <Link to="/custom-containers" style={navItemStyle(location.pathname.startsWith('/custom-containers'))}>Custom Containers</Link>
            <Link to="/spark" style={navItemStyle(location.pathname.startsWith('/spark'))}>Spark</Link>
          </div>
        )}

        {/* Reporting Section */}
        <SectionHeader 
          title="Reporting" 
          icon={FileText} 
          sectionKey="reporting" 
          isExpanded={expandedSections.reporting} 
        />
        {expandedSections.reporting && (
          <div style={{ paddingLeft: 12 }}>
            <Link to="/artifacts" style={navItemStyle(location.pathname.startsWith('/artifacts'))}>Artifacts</Link>
            {/*<Link to="/report-builder" style={navItemStyle(location.pathname.startsWith('/report-builder'))}>Report Builder</Link>*/}
            <Link to="/reports" style={navItemStyle(location.pathname.startsWith('/reports'))}>Reports Directory</Link>
          </div>
        )}

        {/* Insights Section */}
        <SectionHeader 
          title="Insights" 
          icon={Lightbulb} 
          sectionKey="insights" 
          isExpanded={expandedSections.insights} 
        />
        {expandedSections.insights && (
          <div style={{ paddingLeft: 12 }}>
            <Link to="/insights" style={navItemStyle(location.pathname === '/insights')}>Insights Manager</Link>
            <Link to="/insights/chatbot" style={{...navItemStyle(location.pathname.startsWith('/insights/chatbot')), opacity: 0.5, cursor: 'not-allowed'}}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                Chatbot
              </div>
            </Link>
            <Link to="/insights/agent-builder" style={{...navItemStyle(location.pathname.startsWith('/insights/agent-builder')), opacity: 0.5, cursor: 'not-allowed'}}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                Agent Builder
              </div>
            </Link>
              <Link to="/insights/agent-orchestration" style={{...navItemStyle(location.pathname.startsWith('/insights/agent-orchestration')), opacity: 0.5, cursor: 'not-allowed'}}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      Agent Orchestration
                  </div>
              </Link>
              <Link to="/insights/anonymization-rules" style={{...navItemStyle(location.pathname.startsWith('/insights/agent-orchestration')), opacity: 0.5, cursor: 'not-allowed'}}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      Anonymization Rules
                  </div>
              </Link>
          </div>
        )}
        
        {/* Security Section */}
        {isAuthenticated && (
          <>
            <SectionHeader 
              title="Security" 
              icon={Shield} 
              sectionKey="security" 
              isExpanded={expandedSections.security} 
            />
            {expandedSections.security && (
              <div style={{ paddingLeft: 12 }}>
                <Link to="/auth/users" style={navItemStyle(location.pathname.startsWith('/auth/users'))}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    Users
                  </div>
                </Link>
                <Link to="/auth/roles" style={navItemStyle(location.pathname.startsWith('/auth/roles'))}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    Roles
                  </div>
                </Link>
                <Link to="/auth/sso-providers" style={navItemStyle(location.pathname.startsWith('/auth/sso-providers'))}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    SSO Providers
                  </div>
                </Link>
                <Link to="/auth/api-keys" style={navItemStyle(location.pathname.startsWith('/auth/api-keys'))}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    API Keys
                  </div>
                </Link>
                {canViewAuditLogs && (
                  <Link to="/auth/break-glass-incidents" style={navItemStyle(location.pathname.startsWith('/auth/break-glass-incidents'))}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      Break-Glass Incidents
                    </div>
                  </Link>
                )}
                {canViewAuditLogs && (
                  <Link to="/auth/audit-logs" style={navItemStyle(location.pathname.startsWith('/auth/audit-logs'))}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      Audit Logs
                    </div>
                  </Link>
                )}
              </div>
            )}
          </>
        )}
      </nav>
      <div style={{ padding: 12, borderTop: `1px solid ${theme.border}` }}>
        {isAuthenticated && user && (
          <>
            <button
              onClick={() => navigate('/profile')}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                background: theme.background,
                marginBottom: 8,
                border: `1px solid ${theme.border}`,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <User size={16} color={theme.accent} />
                <span style={{ color: theme.text, fontSize: 14, fontWeight: 600 }}>
                  {user.username}
                </span>
              </div>
              <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: userRoles.length > 0 ? 6 : 0 }}>
                {user.email}
              </div>
              {userRoles.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <Shield size={12} color={theme.accent} />
                  <span style={{ 
                    color: theme.accent, 
                    fontSize: 11, 
                    fontWeight: 600,
                    textTransform: 'uppercase'
                  }}>
                    {userRoles.join(', ')}
                  </span>
                </div>
              )}
            </button>
            <button
              onClick={logout}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme.border}`,
                background: theme.cardBackground,
                color: '#ef4444',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 8
              }}
            >
              <LogOut size={18} />
              Logout
            </button>
          </>
        )}
        <button
          onClick={toggleTheme}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: '6px',
            border: `1px solid ${theme.border}`,
            background: theme.cardBackground,
            color: theme.text,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: 14,
            fontWeight: 600
          }}
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          {isDarkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
      </div>
    </aside>
  );
}
